/**
 * Authentication Provider
 * Handles Google OAuth with Supabase and user state management
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase, signInWithGoogle, signOut as supabaseSignOut, isValidDomain, sendOtpToEmail, verifyEmailOtp, signInWithPassword as supabaseSignInWithPassword } from '@/lib/supabase'
import { identifyUser, resetUser, captureException } from '@/lib/posthog'
import { getCurrentMonthDateRange } from '@/lib/utils/date'
import { setInitialEntries } from '@/lib/initial-entries-cache'
import { setupDeepLinkHandler } from '@/lib/capacitor-auth'
import type { User, UserPermissions } from '@/types/database.types'
import type { AuthContextType } from '@/types/app.types'
import type { Session, AuthChangeEvent } from '@supabase/supabase-js'

const AuthContext = createContext<AuthContextType | null>(null)

function SignOutOverlay() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        background: 'var(--bg-primary)',
        animation: 'authOverlayFadeIn 0.3s ease-out',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(var(--accent-primary-rgb), 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'authOverlayPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: 28, height: 28, color: 'var(--accent-primary)' }}
        >
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
          <polyline points="16,17 21,12 16,7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-base)',
          color: 'var(--text-secondary)',
          animation: 'authOverlayTextIn 0.3s ease-out 0.3s both',
        }}
      >
        Signing out&hellip;
      </p>
      <style>{`
        @keyframes authOverlayFadeIn {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        @keyframes authOverlayPop {
          from { opacity: 0; transform: scale(0.5) }
          to { opacity: 1; transform: scale(1) }
        }
        @keyframes authOverlayTextIn {
          from { opacity: 0; transform: translateY(8px) }
          to { opacity: 1; transform: translateY(0) }
        }
      `}</style>
    </div>
  )
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authEvent, setAuthEvent] = useState<{ event: AuthChangeEvent; session: Session | null } | null>(null)
  const initializedRef = useRef(false)
  const queryClient = useQueryClient()

  // Fetch user profile from our users table
  const fetchUserProfile = useCallback(async (authUserId: string, email: string) => {
    // Validate domain
    if (!isValidDomain(email)) {
      await supabaseSignOut()
      throw new Error('Access restricted to Zest Dental employees')
    }

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', authUserId)
      .single()

    if (error) {
      // User might not exist yet (first login) - trigger will create them
      // Wait a bit and retry
      if (error.code === 'PGRST116') {
        await new Promise(resolve => setTimeout(resolve, 1000))
        const { data: retryData, error: retryError } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUserId)
          .single()

        if (retryError) {
          // User still doesn't exist after retry — likely auto-signup is off
          // and this user was not invited
          if (retryError.code === 'PGRST116') {
            await supabaseSignOut()
            const notSetupError = new Error(
              'Your account has not been set up yet. Please contact your administrator.'
            )
            ;(notSetupError as Error & { isAuthSetupError?: boolean }).isAuthSetupError = true
            throw notSetupError
          }
          console.error('Failed to fetch user after retry:', retryError)
          throw retryError
        }
        return retryData
      }
      throw error
    }

    return data
  }, [])

  // Fetch user permissions from the get_user_permissions RPC
  const fetchUserPermissions = useCallback(async (userId: string): Promise<UserPermissions | null> => {
    try {
      const { data, error } = await supabase.rpc('get_user_permissions', {
        p_user_id: userId,
      })

      if (error) {
        console.error('Failed to fetch user permissions:', error)
        return null
      }

      return data as unknown as UserPermissions
    } catch (error) {
      console.error('Error fetching user permissions:', error)
      return null
    }
  }, [])

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()

        if (session?.user) {
          const profile = await fetchUserProfile(session.user.id, session.user.email || '')

          // Fetch entries in parallel with permissions while the JS event loop
          // is still active. On iOS WKWebView cold start, the event loop freezes
          // after the initial render — fetching here ensures data is ready before
          // EntryList renders.
          const { start, end } = getCurrentMonthDateRange()
          const entryKey = ['entries', 'list', profile.id, start, end]
          const [permissions, entriesResult] = await Promise.all([
            fetchUserPermissions(profile.id),
            supabase
              .from('entries')
              .select('*, activity:activities(*), customer:customers(*)')
              .eq('user_id', profile.id)
              .gte('date', start)
              .lte('date', end)
              .order('created_at', { ascending: false })
              .then(({ data, error }) => error ? null : data),
          ])

          if (entriesResult) {
            queryClient.setQueryData(entryKey, entriesResult)
            setInitialEntries(entriesResult, start, end)
          }

          setUser(profile)
          setUserPermissions(permissions)

          identifyUser(profile.id, {
            email: profile.email,
            name: profile.name,
            role: profile.role,
            is_active: profile.is_active,
          })

          // Update last login
          supabase
            .from('users')
            .update({ last_login_at: new Date().toISOString() })
            .eq('id', session.user.id)
            .then(() => {}) // Fire and forget
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        setUser(null)
        setUserPermissions(null)
        if (error instanceof Error && (error as Error & { isAuthSetupError?: boolean }).isAuthSetupError) {
          setAuthError(error.message)
        }
        if (error instanceof Error) {
          captureException(error, { source: 'auth_initialization' })
        }
      } finally {
        setIsLoading(false)
        initializedRef.current = true
      }
    }

    initAuth()

    // Listen for auth changes - MUST be synchronous to avoid deadlock
    // See: https://github.com/supabase/auth-js/issues/762
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only process events after initial load to avoid race conditions
        if (initializedRef.current) {
          setAuthEvent({ event, session })
        }
      }
    )

    // Handle deep link callbacks from OAuth on native platforms (Capacitor)
    setupDeepLinkHandler(async (url) => {
      if (url.includes('code=')) {
        const urlObj = new URL(url)
        const code = urlObj.searchParams.get('code')
        if (code) {
          await supabase.auth.exchangeCodeForSession(code)
        }
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [fetchUserProfile, fetchUserPermissions, queryClient])

  // Handle auth events in a separate effect (async operations safe here)
  useEffect(() => {
    if (!authEvent) return

    const { event, session } = authEvent

    const handleAuthChange = async () => {
      if (event === 'SIGNED_IN' && session?.user) {
        try {
          const profile = await fetchUserProfile(session.user.id, session.user.email || '')

          const { start, end } = getCurrentMonthDateRange()
          const [permissions, entriesResult] = await Promise.all([
            fetchUserPermissions(profile.id),
            supabase
              .from('entries')
              .select('*, activity:activities(*), customer:customers(*)')
              .eq('user_id', profile.id)
              .gte('date', start)
              .lte('date', end)
              .order('created_at', { ascending: false })
              .then(({ data, error }) => error ? null : data),
          ])

          if (entriesResult) {
            queryClient.setQueryData(['entries', 'list', profile.id, start, end], entriesResult)
            setInitialEntries(entriesResult, start, end)
          }

          identifyUser(profile.id, {
            email: profile.email,
            name: profile.name,
            role: profile.role,
            is_active: profile.is_active,
          })

          // Update last login
          supabase
            .from('users')
            .update({ last_login_at: new Date().toISOString() })
            .eq('id', session.user.id)
            .then(() => {}) // Fire and forget

          // Brief delay on login page so the success animation can play
          // before setUser triggers redirect via route beforeLoad
          if (window.location.pathname === '/login') {
            await new Promise(resolve => setTimeout(resolve, 1200))
          }

          setUser(profile)
          setUserPermissions(permissions)
        } catch (error) {
          console.error('Error fetching user profile:', error)
          setUser(null)
          setUserPermissions(null)
          if (error instanceof Error && (error as Error & { isAuthSetupError?: boolean }).isAuthSetupError) {
            setAuthError(error.message)
          }
          if (error instanceof Error) {
            captureException(error, { source: 'auth_state_change' })
          }
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setUserPermissions(null)
        setIsSigningOut(false)
        resetUser()
        // Full page reload is intentional: AuthProvider sits above RouterProvider
        // in the component tree so router.navigate() is unavailable. The reload
        // also ensures all in-memory state (queries, permissions, timers) is cleared.
        window.location.href = '/login'
      }
    }

    handleAuthChange()
  }, [authEvent, fetchUserProfile, fetchUserPermissions, queryClient])

  // Sign in handler (Google OAuth)
  const signIn = useCallback(async () => {
    try {
      setAuthError(null)
      await signInWithGoogle()
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    }
  }, [])

  // Sign in with email (sends OTP)
  const signInWithEmail = useCallback(async (email: string) => {
    try {
      setAuthError(null)
      await sendOtpToEmail(email)
    } catch (error) {
      console.error('Email sign in error:', error)
      throw error
    }
  }, [])

  // Verify OTP code
  const verifyOtp = useCallback(async (email: string, token: string) => {
    try {
      await verifyEmailOtp(email, token)
      // Auth state change listener will handle the rest
    } catch (error) {
      console.error('OTP verification error:', error)
      throw error
    }
  }, [])

  // Sign in with password (demo mode)
  const signInWithPassword = useCallback(async (email: string, password: string) => {
    try {
      setAuthError(null)
      await supabaseSignInWithPassword(email, password)
      // Auth state change listener will handle the rest
    } catch (error) {
      console.error('Password sign in error:', error)
      throw error
    }
  }, [])

  // Sign out handler — shows transition overlay before clearing state
  const signOut = useCallback(async () => {
    try {
      setIsSigningOut(true)
      // Let the overlay animation play before clearing state
      await new Promise(resolve => setTimeout(resolve, 1000))
      await supabaseSignOut()
      setUser(null)
      setUserPermissions(null)
      resetUser()
    } catch (error) {
      console.error('Sign out error:', error)
      setIsSigningOut(false)
      throw error
    }
  }, [])

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error
      setUser(data)

      // Also refresh permissions
      const permissions = await fetchUserPermissions(data.id)
      setUserPermissions(permissions)
    } catch (error) {
      console.error('Error refreshing user:', error)
      if (error instanceof Error) {
        captureException(error, { source: 'auth_user_refresh' })
      }
    }
  }, [user, fetchUserPermissions])

  const value: AuthContextType = {
    user,
    userPermissions,
    isLoading,
    isSigningOut,
    isAuthenticated: !!user,
    authError,
    signIn,
    signInWithEmail,
    verifyOtp,
    signInWithPassword,
    signOut,
    refreshUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {isSigningOut && <SignOutOverlay />}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
